import { useLayoutEffect, useState } from 'react';

import useDevice from 'hooks/useDevice';

import type { CopyFont, Measure, UseAutosizeInputFlexBasisProps } from './types';

const copyFont: CopyFont = ({ hiddenElement, inputElement }) => {
    const computedStyle = getComputedStyle(inputElement);

    hiddenElement.style.font = computedStyle.font;
    hiddenElement.style.fontStretch = computedStyle.fontStretch;
    hiddenElement.style.fontKerning = computedStyle.fontKerning;
    hiddenElement.style.fontVariant = computedStyle.fontVariant;
    hiddenElement.style.fontVariantNumeric = computedStyle.fontVariantNumeric;
    hiddenElement.style.fontVariationSettings = computedStyle.fontVariationSettings;
    hiddenElement.style.fontOpticalSizing = computedStyle.fontOpticalSizing;
    hiddenElement.style.letterSpacing = computedStyle.letterSpacing;
    hiddenElement.style.wordSpacing = computedStyle.wordSpacing;
    hiddenElement.style.textTransform = computedStyle.textTransform;
};

const measure: Measure = ({ cell, hiddenElement, input }) => {
    const computedStyle = getComputedStyle(input);

    hiddenElement.textContent = (input.value || input.placeholder || '') + '\u200A';
    let width = Math.ceil(hiddenElement.getBoundingClientRect().width);

    if (computedStyle.boxSizing === 'border-box') {
        width +=
            parseFloat(computedStyle.paddingLeft) +
            parseFloat(computedStyle.paddingRight) +
            parseFloat(computedStyle.borderLeftWidth) +
            parseFloat(computedStyle.borderRightWidth);
    }
    cell.style.flexBasis = Math.max(64, width) + 'px';
};

const useAutosizeInputFlexBasis = ({
    flexRowRef,
    inputContainerRef,
    inputSelector = 'input'
}: UseAutosizeInputFlexBasisProps) => {
    const { devices } = useDevice();
    const { deviceType } = devices;
    const [inputElement, setInputElement] = useState<HTMLInputElement | null>(null);

    useLayoutEffect(() => {
        const cell = inputContainerRef.current;

        if (!cell) {
            setInputElement(null);

            return;
        }

        const updateInputElement = () => {
            const input = cell.querySelector<HTMLInputElement>(inputSelector);

            setInputElement(input);
        };

        updateInputElement();

        const observer = new MutationObserver(() => {
            updateInputElement();
        });

        observer.observe(cell, {
            attributeFilter: ['type', 'class', 'id'],
            attributes: true,
            childList: true,
            subtree: true
        });

        return () => {
            observer.disconnect();
        };
    }, [inputContainerRef, inputSelector]);

    useLayoutEffect(() => {
        if (deviceType === 'mobile') {
            return;
        }

        const row = flexRowRef.current;
        const cell = inputContainerRef.current;

        if (!row || !cell || !inputElement) {
            return;
        }

        const hiddenElement = document.createElement('span');

        Object.assign(hiddenElement.style, {
            left: '0',
            position: 'fixed',
            top: '-9999px',
            visibility: 'hidden',
            whiteSpace: 'pre'
        } as CSSStyleDeclaration);
        document.body.appendChild(hiddenElement);

        let requestedAnimationFrame = 0;

        const schedule = () => {
            cancelAnimationFrame(requestedAnimationFrame);

            requestedAnimationFrame = requestAnimationFrame(() =>
                measure({ cell, hiddenElement, input: inputElement })
            );
        };

        copyFont({ hiddenElement, inputElement });
        schedule();

        inputElement.addEventListener('input', schedule);

        document.fonts?.ready.then(schedule);

        const onFonts = () => schedule();

        document.fonts?.addEventListener?.('loadingdone', onFonts);

        const resizeObserver = new ResizeObserver(schedule);

        resizeObserver.observe(row);

        const resizeObserverInput = new ResizeObserver(() => {
            copyFont({ hiddenElement, inputElement });
            schedule();
        });

        resizeObserverInput.observe(inputElement);

        return () => {
            inputElement.removeEventListener('input', schedule);
            document.fonts?.removeEventListener?.('loadingdone', onFonts);
            resizeObserver.disconnect();
            resizeObserverInput.disconnect();
            cancelAnimationFrame(requestedAnimationFrame);
            hiddenElement.remove();
        };
    }, [flexRowRef, inputContainerRef, deviceType, inputElement]);
};

export default useAutosizeInputFlexBasis;
